import React, { useEffect, useState } from 'react';
import { Instagram, Facebook } from 'lucide-react';
import api from '../utils/api';
import TwineCandlesLogo from './TwineCandlesLogo';

interface ContactInfo {
  whatsappNumber: string;
  instagramUrl: string;
  facebookUrl: string;
  phoneNumber: string;
}

const initialContactInfo: ContactInfo = {
  whatsappNumber: '',
  instagramUrl: '',
  facebookUrl: '',
  phoneNumber: ''
};

const getWhatsAppUrl = (value: string): string => {
  const normalized = value.replace(/[^\d]/g, '');
  return normalized ? `https://wa.me/${normalized}` : '';
};

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M20.52 3.48A11.79 11.79 0 0 0 12.06 0C5.53 0 .22 5.3.22 11.82c0 2.08.54 4.11 1.57 5.9L0 24l6.46-1.69a11.79 11.79 0 0 0 5.6 1.43h.01c6.53 0 11.84-5.31 11.84-11.84 0-3.16-1.23-6.13-3.39-8.42ZM12.07 21.7h-.01a9.8 9.8 0 0 1-4.99-1.36l-.36-.21-3.84 1 1.02-3.74-.24-.39a9.78 9.78 0 0 1-1.5-5.2c0-5.42 4.41-9.83 9.84-9.83 2.63 0 5.11 1.03 6.97 2.9a9.78 9.78 0 0 1 2.87 6.95c0 5.42-4.41 9.83-9.84 9.83Zm5.39-7.34c-.29-.15-1.73-.85-2-.95-.27-.1-.47-.15-.67.15s-.77.95-.94 1.15c-.17.2-.35.22-.64.07-.29-.15-1.22-.45-2.33-1.43-.86-.77-1.45-1.71-1.62-2-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.08-.79.37-.27.29-1.03 1-1.03 2.44s1.05 2.83 1.2 3.03c.15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.11.56-.08 1.73-.71 1.98-1.39.25-.69.25-1.27.17-1.39-.07-.12-.27-.2-.56-.35Z"
    />
  </svg>
);

const SiteFooter: React.FC = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>(initialContactInfo);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const { data } = await api.get('/api/contact-info');
        setContactInfo({
          whatsappNumber: data?.whatsappNumber || '',
          instagramUrl: data?.instagramUrl || '',
          facebookUrl: data?.facebookUrl || '',
          phoneNumber: data?.phoneNumber || ''
        });
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };

    fetchContactInfo();
  }, []);

  const whatsappUrl = getWhatsAppUrl(contactInfo.whatsappNumber);
  const hasAnyContact = Boolean(whatsappUrl || contactInfo.instagramUrl || contactInfo.facebookUrl || contactInfo.phoneNumber);

  return (
    <footer className="relative border-t-2 border-secondary-200 dark:border-dark-border bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50 dark:bg-none dark:bg-dark-navbar transition-all duration-300 overflow-visible shadow-[0_-8px_14px_rgba(15,23,42,0.10)] dark:shadow-[0_-8px_14px_rgba(0,0,0,0.45)]">
      <div className="pointer-events-none absolute top-0 inset-x-0 h-3 bg-gradient-to-b from-secondary-200/70 to-transparent dark:from-dark-navbar dark:to-transparent" />

      <div className="relative z-10 w-full px-3 sm:px-4 md:px-10 lg:px-16 py-6">
        <div className="w-full">
          <div dir="ltr" className="flex flex-col items-center gap-5 text-center md:grid md:grid-cols-3 md:items-center md:gap-4">
            <div dir="rtl" className="md:order-3 flex flex-col items-center gap-2 md:justify-self-end md:text-right">
              <p className="text-sm sm:text-base text-black dark:text-dark-text">
                {'\u062C\u0645\u064A\u0639 \u0627\u0644\u062D\u0642\u0648\u0642 \u0645\u062D\u0641\u0648\u0638\u0629'}
              </p>
              <p className="inline-flex items-center gap-1 text-sm sm:text-base text-black dark:text-dark-text">
                <span>{'\u0641\u0644\u0633\u0637\u064A\u0646'}</span>
                <span>{'\uD83C\uDDF5\uD83C\uDDF8'}</span>
              </p>
            </div>

            <div dir="rtl" className="md:order-2 flex flex-col items-center gap-3 md:justify-self-center">
              <p className="text-sm sm:text-base font-semibold text-black dark:text-dark-text">
                {'\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627:'}
              </p>

              {hasAnyContact ? (
                <>
                  <div className="inline-flex items-center justify-center gap-4">
                    {whatsappUrl && (
                      <a href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="flex h-6 w-6 items-center justify-center text-green-600 hover:opacity-80 transition-opacity">
                        <WhatsAppIcon className="h-5 w-5" />
                      </a>
                    )}

                    {contactInfo.instagramUrl && (
                      <a href={contactInfo.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram" className="flex h-6 w-6 items-center justify-center text-pink-600 hover:opacity-80 transition-opacity">
                        <Instagram className="h-5 w-5" />
                      </a>
                    )}

                    {contactInfo.facebookUrl && (
                      <a href={contactInfo.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook" className="flex h-6 w-6 items-center justify-center text-blue-600 hover:opacity-80 transition-opacity">
                        <Facebook className="h-5 w-5" />
                      </a>
                    )}
                  </div>

                  {contactInfo.phoneNumber && (
                    <a href={`tel:${contactInfo.phoneNumber}`} className="text-sm text-black dark:text-dark-text hover:opacity-80 transition-opacity">
                      {contactInfo.phoneNumber}
                    </a>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-dark-textSecondary">
                  {'\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u062A\u0648\u0627\u0635\u0644 \u062D\u0627\u0644\u064A\u0627\u064B.'}
                </p>
              )}
            </div>

            <div className="md:order-1 md:justify-self-start">
              <TwineCandlesLogo size="xl" className="justify-center md:justify-start" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
